import type { CrmBoardQuery, CrmCityDto, CrmDealDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@upme/ui/components/dialog';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useCreateCrmManualLead } from '../mutations';

const NO_CITY = '__none__';

type Props = {
  filters: CrmBoardQuery;
  cities: ReadonlyArray<CrmCityDto>;
  onCreated?: (deal: CrmDealDto) => void;
};

export function CrmAddLeadDialog({ filters, cities, onCreated }: Props) {
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [telegramUsername, setTelegramUsername] = useState('');
  const [instagramUsername, setInstagramUsername] = useState('');
  const [note, setNote] = useState('');
  const [cityId, setCityId] = useState<string>(NO_CITY);

  const create = useCreateCrmManualLead(filters);

  function resetForm() {
    setDisplayName('');
    setPhone('');
    setTelegramUsername('');
    setInstagramUsername('');
    setNote('');
    setCityId(NO_CITY);
  }

  async function submit() {
    const name = displayName.trim();
    if (!name) return;
    const deal = await create.mutateAsync({
      displayName: name,
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(telegramUsername.trim() ? { telegramUsername: telegramUsername.trim() } : {}),
      ...(instagramUsername.trim() ? { instagramUsername: instagramUsername.trim() } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(cityId !== NO_CITY ? { cityId } : {}),
    });
    resetForm();
    setOpen(false);
    onCreated?.(deal);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" size="sm">
          <Plus className="mr-1 size-4" aria-hidden />
          Добавить лида
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Новый лид</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="crm-lead-name">Имя или название</Label>
            <Input
              id="crm-lead-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Иван или Studio Events"
              autoFocus
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="crm-lead-phone">Телефон</Label>
            <Input
              id="crm-lead-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+995 …"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="crm-lead-tg">Telegram</Label>
            <Input
              id="crm-lead-tg"
              value={telegramUsername}
              onChange={(e) => setTelegramUsername(e.target.value)}
              placeholder="@username"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="crm-lead-ig">Instagram</Label>
            <Input
              id="crm-lead-ig"
              value={instagramUsername}
              onChange={(e) => setInstagramUsername(e.target.value)}
              placeholder="@username"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="crm-lead-city">Город</Label>
            <Select value={cityId} onValueChange={setCityId}>
              <SelectTrigger id="crm-lead-city">
                <SelectValue placeholder="Не указан" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CITY}>Не указан</SelectItem>
                {cities.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="crm-lead-note">Заметка</Label>
            <textarea
              id="crm-lead-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Откуда пришёл, что интересует"
              rows={3}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Отмена
          </Button>
          <Button
            type="button"
            disabled={!displayName.trim() || create.isPending}
            onClick={() => void submit()}
          >
            {create.isPending ? 'Сохраняем…' : 'Добавить на доску'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
